import { useState } from 'react';
import { Alert, FormControlLabel, Stack, Switch } from '@mui/material';

import AssigneeSelect from './AssigneeSelect';
import GhostButton from '../../shared/button/GhostButton';
import { MinorHeading } from '../../shared/typography/PrimaryTypography';
import {
  assignRecord,
  flagRecord,
  requestSpecialist,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { EsgRecord, ProblemDetail } from '../../../types/syntiro_honey';

interface RecordMarkersProps {
  /** The record whose markers are being edited. */
  record: EsgRecord;
  /** Called with the updated record after each successful marker change. */
  onChanged: (updated: EsgRecord) => void;
}

/**
 * Controls for the orthogonal record markers: follow-up flag, EP-specialist
 * request and reviewer assignment (picked from the platform's user directory;
 * clearing the selection unassigns). Each change calls its dedicated endpoint and
 * applies the returned record.
 * @returns {JSX.Element}
 */
export default function RecordMarkers({ record, onChanged }: RecordMarkersProps) {
  const [assigneeDraft, setAssigneeDraft] = useState<string | null>(record.assignedToId);
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  const runMarkerAction = async (action: () => Promise<EsgRecord>): Promise<void> => {
    setBusy(true);
    try {
      const updated = await action();
      setProblem(null);
      onChanged(updated);
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setBusy(false);
    }
  };

  const assigneeChanged = assigneeDraft !== record.assignedToId;

  return (
    <Stack spacing={1.5}>
      <MinorHeading weight="Medium">Markers</MinorHeading>
      {problem !== null && (
        <Alert severity="error">{problem.detail ?? problem.title ?? 'Marker update failed.'}</Alert>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={record.needsFollowUp}
            disabled={busy}
            onChange={(event) =>
              void runMarkerAction(() =>
                flagRecord(record.id, { needsFollowUp: event.target.checked })
              )
            }
          />
        }
        label="Needs follow-up"
      />
      <FormControlLabel
        control={
          <Switch
            checked={record.specialistRequested}
            disabled={busy}
            onChange={(event) =>
              void runMarkerAction(() =>
                requestSpecialist(record.id, { specialistRequested: event.target.checked })
              )
            }
          />
        }
        label="EP-specialist validation requested"
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <AssigneeSelect
          value={assigneeDraft}
          valueName={record.assignedToName}
          disabled={busy}
          onChange={setAssigneeDraft}
        />
        <GhostButton
          dark
          outlined
          disabled={busy || !assigneeChanged}
          onClick={() =>
            void runMarkerAction(() => assignRecord(record.id, { assignedToId: assigneeDraft }))
          }
        >
          {assigneeDraft === null ? 'Clear' : 'Assign'}
        </GhostButton>
      </Stack>
    </Stack>
  );
}
